import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AppProvider } from "@/context/AppContext";
import { Header } from "@/components/Header";
import { Sidebar } from "@/components/Sidebar";

import Dashboard  from "@/components/tabs/Dashboard";
import Controls   from "@/components/tabs/Controls";
import Energy     from "@/components/tabs/Energy";
import Grafana    from "@/components/tabs/Grafana";
import SettingsTab from "@/components/tabs/SettingsTab";
import Personalize from "@/components/tabs/Personalize";
import CameraTab  from "@/components/tabs/CameraTab";
import GalleryTab from "@/components/tabs/GalleryTab";
import ProfileTab from "@/components/tabs/ProfileTab";
import RoboFaceCall from "@/components/tabs/RoboFaceCall";
import AboutTab   from "@/components/tabs/AboutTab";
import ContactTab from "@/components/tabs/ContactTab";

const TAB_COMPONENTS = {
  dashboard:  Dashboard,
  controls:   Controls,
  energy:     Energy,
  grafana:    Grafana,
  settings:   SettingsTab,
  personalize: Personalize,
  camera:     CameraTab,
  gallery:    GalleryTab,
  roboface:   RoboFaceCall,
  about:      AboutTab,
  contact:    ContactTab,
  profile:    ProfileTab,
};

const VALID_TABS = Object.keys(TAB_COMPONENTS);

function Shell() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (() => { const t = searchParams.get("tab"); return VALID_TABS.includes(t) ? t : "dashboard"; })();
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem("cem_sidebar_collapsed");
    if (saved !== null) return saved === "true";
    return typeof window !== "undefined" ? window.innerWidth < 1024 : true;
  });
  const isMobile = () => typeof window !== "undefined" && window.innerWidth < 1024;
  const toggleSidebar = () => setCollapsed((c) => {
    localStorage.setItem("cem_sidebar_collapsed", String(!c));
    return !c;
  });
  const handleTabChange = (newTab) => {
    setSearchParams({ tab: newTab }, { replace: true });
    if (isMobile()) setCollapsed(true);
  };

  useEffect(() => {
    const shouldLock = !collapsed && isMobile();
    document.body.style.overflow = shouldLock ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [collapsed]);

  const Active = TAB_COMPONENTS[tab];
  return (
    <div className="min-h-[100dvh] bg-[var(--bg)] text-[var(--t1)] flex">
      {!collapsed && (
        <div
          className="fixed inset-0 z-40 bg-black/60 lg:hidden"
          onClick={toggleSidebar}
          aria-hidden
        />
      )}
      <Sidebar active={tab} onChange={handleTabChange} collapsed={collapsed} onToggle={toggleSidebar} />

      {collapsed && (
        <button
          onClick={toggleSidebar}
          className="fixed bottom-5 left-4 z-50 lg:hidden w-11 h-11 rounded-full bg-[var(--accent)] text-black flex items-center justify-center shadow-lg shadow-[var(--accent)]/30 active:scale-95 transition-transform"
          aria-label="Open sidebar"
        >
          <span className="text-lg leading-none">☰</span>
        </button>
      )}

      <div className="flex-1 min-w-0 flex flex-col min-h-[100dvh]">
        <Header collapsed={collapsed} onToggle={toggleSidebar} />
        <main className="flex-1 w-full max-w-[1600px] mx-auto px-4 sm:px-6 xl:px-10 py-5 sm:py-6" data-testid={`panel-${tab}`}>
          <Active key={tab} />
        </main>
        <footer className="border-t border-[var(--bd)] mt-8 py-6 px-4 text-center text-[11px] sm:text-xs text-[var(--t2)] font-mono">
          PZEM Energy Monitor · ESP32 + PZEM-004T ×3 · HiveMQ Cloud · © 2026 Henock Mukonkole · CPUT
        </footer>
      </div>
    </div>
  );
}

export default function DashboardApp() {
  return (
    <AppProvider>
      <Shell />
    </AppProvider>
  );
}
